import PptxGenJS from 'pptxgenjs'

import { ApiAccess } from 'features/session/types'

import { SearchDetailResult } from 'interface/search'
import { KojinResult } from 'interface/kojin'
import { getWebLogTags } from 'interface/kojin/log'
import { GetAvatarImage } from 'interface/kojin/avatar'
import { GetAuthor } from 'interface/author'
import { ArticleResult, ListArticleByAuthor } from 'interface/article'

import { KojinPptxComponentsProps, KojinPptxFormat, LayoutOption } from '../types'

import { header } from '../components/header'
import { sisetuList, buildBlockData } from '../components/sisetuList'
import { authorsIndex } from '../components/authosIndex'
import { organList } from '../components/organList'
import { attributes } from '../components/attributes'
import { diseaseRadar } from '../components/diseaseRadar'
import { drugScoreTable } from '../components/drugScoreTable'
import { CareNet, SystemName } from 'constants/captions'
import { getJournalDate } from 'features/article/util'

export const format: KojinPptxFormat = {
  reportId: 'chugai',
  reportName: '中外製薬社向け',
  domain: ['carenet', 'chugai'],
  record: async (
    fileName: string,
    props: {
      apiAccess: ApiAccess
      search: SearchDetailResult
      kojinList: KojinResult[]
    },
    onProgress?: (percent: number) => void
  ) => {
    const { apiAccess, search, kojinList } = props

    const authorList = []
    for (const kojin of kojinList) {
      if (kojin.authorId) {
        const author = await GetAuthor(
          apiAccess,
          search.searchId,
          kojin.authorId
        )
        if (author) {
          const articles = await ListArticleByAuthor(
            apiAccess,
            search.searchId,
            kojin.authorId,
            { updateTime: search.updateTime }
          )
          author.article = articles.list.filter(
            (article) => article.authorType === 1 || article.authorType === 2
          ) ?? []
          articles.list.sort((a, b) =>
            getJournalDate(b).localeCompare(getJournalDate(a))
          )
          authorList.push(author)
        }
      }
    }

    const slideSize = {
      name: 'A4',
      width: 11.69,
      height: 8.27,
    }
    const baseFont = {
      fontFace: '游ゴシック Medium',
      fontSize: 10.5,
    }
    const textOption: PptxGenJS.TextPropsOptions = {
      ...baseFont,
      color: '000000',
    }
    const cellOption: PptxGenJS.TableCellProps = {
      ...textOption,
      margin: [0.05, 0.1, 0.05, 0.1],
    }
    const layout: LayoutOption = {
      baseFont: baseFont,
      textOption: textOption,
      cellOption: cellOption,
      slideSize: slideSize,
      margin: 0.2,
      footerHeight: 0.3,
      inchPerDot: 94,
    }

    const pptx = new PptxGenJS()
    pptx.company = CareNet
    pptx.author = SystemName
    pptx.revision = '1'
    pptx.title = fileName
    pptx.subject = fileName
    pptx.defineLayout({
      name: slideSize.name,
      width: slideSize.width,
      height: slideSize.height,
    })
    pptx.layout = slideSize.name
    pptx.theme = {
      headFontFace: baseFont.fontFace,
      bodyFontFace: baseFont.fontFace,
    }
    pptx.defineSlideMaster({
      title: 'MASTER_SLIDE',
      background: { color: 'FFFFFF' },
      objects: [
        {
          rect: {
            x: 0,
            y: slideSize.height - layout.footerHeight,
            w: slideSize.width,
            h: layout.footerHeight,
            fill: { color: 'bcbdc0' },
          },
        },
        {
          image: {
            x: 0,
            y: slideSize.height - layout.footerHeight,
            w: (layout.footerHeight * 663) / 90,
            h: layout.footerHeight,
            path: `${process.env.PUBLIC_URL}/slide_footer.png`,
          },
        },
      ],
      slideNumber: {
        ...textOption,
        x: slideSize.width - 0.5,
        y: slideSize.height - layout.footerHeight,
        w: 0.5,
        h: layout.footerHeight,
        fontSize: baseFont.fontSize * 0.8,
        align: 'center',
        valign: 'middle',
      },
      margin: [0, 0, layout.footerHeight, 0],
    })

    const componentProps: KojinPptxComponentsProps = {
      pptx, layout, 
      slide: pptx.addSlide({ masterName: 'MASTER_SLIDE' }),
      search
    }
    if (kojinList.length > 1) {
      authorsIndex({
        ...componentProps,
        title: fileName,
        kojinList
      })
      const blockList = buildBlockData(kojinList)
      if (blockList.length > 0) {
        componentProps.slide = pptx.addSlide({ masterName: 'MASTER_SLIDE' })
        sisetuList({
          ...componentProps,
          title: fileName,
          blockList
        })
      }
    }
    let n = 0
    for (const kojin of kojinList) {
      componentProps.slide = pptx.addSlide({ masterName: 'MASTER_SLIDE' })
      componentProps.kojin = kojin
      componentProps.avatarImage = await GetAvatarImage(
        apiAccess,
        kojin.kojinCode
      ).catch(() => undefined)

      let pos = 0
      pos = header({ ...componentProps, top: pos }).bottom
      pos = Math.max(
        organList({ ...componentProps, top: pos }).bottom,
        attributes({ ...componentProps, top: pos }).bottom
      )
      const tagStatics = kojin.personalId ? (
        await getWebLogTags(apiAccess, kojin.personalId)
      ) : undefined
      pos = Math.max(
        diseaseRadar({ ...componentProps, top: pos }).bottom,
        drugScoreTable({ ...componentProps, drugTags: tagStatics?.drug ?? [], top: pos }).bottom
      )

      const articles: ArticleResult[] = []
      const author = kojin.authorId ? authorList.find(author => author.authorId === kojin.authorId) : undefined
      if (author && author.article && author.article.length > 0) {
        articles.push(...author.article)
      }
      //approach(pptx, layout, slide, kojin, avatarImage, author, pos)
      n++
      if (onProgress) onProgress((n * 100) / kojinList.length)
    }
    await pptx.writeFile({ fileName: `${fileName}.pptx` })
    return false
  }
}
